import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Send } from 'lucide-react';

const ConsultationForm = () => {
  const { language } = useLanguage();
  const t = content[language];
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API call (mock)
    setTimeout(() => {
      // Save to localStorage for mock
      const consultations = JSON.parse(localStorage.getItem('consultations') || '[]');
      consultations.push({
        ...formData,
        id: Date.now(),
        date: new Date().toISOString(),
        status: 'pending'
      });
      localStorage.setItem('consultations', JSON.stringify(consultations));

      toast({
        title: language === 'ar' ? 'تم الإرسال!' : 'Success!',
        description: t.consultation.success,
      });

      setFormData({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: ''
      });
      setLoading(false);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="consultation" className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 bg-amber-50 rounded-full mb-6">
            <span className="text-amber-600 font-semibold text-sm">{t.consultation.title}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {t.consultation.title}
          </h2>
          <p className="text-lg text-slate-600">
            {t.consultation.subtitle}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-3xl p-8 md:p-12 shadow-2xl border border-slate-200">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label htmlFor="name" className="text-slate-700 mb-2 block font-medium">
                {t.consultation.form.name}
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-white border-slate-300 focus:border-amber-500 focus:ring-amber-500 h-12"
                placeholder={t.consultation.form.name}
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-slate-700 mb-2 block font-medium">
                {t.consultation.form.phone}
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                className="bg-white border-slate-300 focus:border-amber-500 focus:ring-amber-500 h-12"
                placeholder="+965 XXXXXXXX"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label htmlFor="email" className="text-slate-700 mb-2 block font-medium">
                {t.consultation.form.email}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-white border-slate-300 focus:border-amber-500 focus:ring-amber-500 h-12"
                placeholder="example@email.com"
              />
            </div>
            <div>
              <Label htmlFor="service" className="text-slate-700 mb-2 block font-medium">
                {t.consultation.form.service}
              </Label>
              <Select onValueChange={(value) => setFormData({ ...formData, service: value })} value={formData.service} required>
                <SelectTrigger className="bg-white border-slate-300 focus:border-amber-500 focus:ring-amber-500 h-12">
                  <SelectValue placeholder={t.consultation.form.selectService} />
                </SelectTrigger>
                <SelectContent>
                  {t.services.items.map((service) => (
                    <SelectItem key={service.id} value={service.title}>
                      {service.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mb-8">
            <Label htmlFor="message" className="text-slate-700 mb-2 block font-medium">
              {t.consultation.form.message}
            </Label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="bg-white border-slate-300 focus:border-amber-500 focus:ring-amber-500 resize-none"
              placeholder={t.consultation.form.message}
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg shadow-xl hover:shadow-2xl transition-all transform hover:scale-[1.02] group"
          >
            {loading ? (
              <span>{language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}</span>
            ) : (
              <>
                {t.consultation.form.submit}
                <Send className={`w-5 h-5 ${language === 'ar' ? 'mr-2' : 'ml-2'} group-hover:translate-x-1 transition-transform`} />
              </>
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default ConsultationForm;